import type { Location, NavigateFunction } from 'react-router-dom';

export type AuthLocationState = {
  from?: string;
  authPromptDismissed?: boolean;
};

export function isAuthPromptDismissed(state: unknown): boolean {
  return Boolean((state as AuthLocationState | null)?.authPromptDismissed);
}

/** Where to send the user when they cancel the login modal (avoid auth-gated loops). */
export function loginCancelDestination(from: string): { pathname: string; search: string } {
  if (!from || from === '/login' || from === '/register') {
    return { pathname: '/', search: '' };
  }

  let pathname = from;
  let search = '';
  const q = from.indexOf('?');
  if (q >= 0) {
    pathname = from.slice(0, q);
    search = from.slice(q);
  }

  // Nested event sub-routes → public event page
  const nestedEvent = pathname.match(
    /^\/events\/([^/]+)\/([^/]+)\/(register|tickets|payment\/return|tickets\/return|archive)$/
  );
  if (nestedEvent) {
    return { pathname: `/events/${nestedEvent[1]}/${nestedEvent[2]}`, search: '' };
  }

  // Legacy single-segment event sub-routes → public event page
  const legacyEvent = pathname.match(
    /^\/events\/([^/]+)\/(register|tickets|payment\/return|tickets\/return|archive)$/
  );
  if (legacyEvent) {
    return { pathname: `/events/${legacyEvent[1]}`, search: '' };
  }

  // Auth-only destinations → home
  if (
    pathname === '/favorites' ||
    pathname.startsWith('/passport') ||
    pathname.startsWith('/garage') ||
    pathname.startsWith('/organizer') ||
    pathname.startsWith('/judge')
  ) {
    return { pathname: '/', search: '' };
  }

  return { pathname, search };
}

export function redirectToLogin(
  navigate: NavigateFunction,
  from: string,
  options?: { replace?: boolean }
): void {
  navigate('/login', { state: { from }, replace: options?.replace ?? false });
}

export function shouldPromptForLogin(
  isAuthenticated: boolean,
  authLoading: boolean,
  location: Location
): boolean {
  if (authLoading || isAuthenticated) return false;
  return !isAuthPromptDismissed(location.state);
}
